<template>
    <div>
        <div class="flex flex-col min-h-0 h-full p-6 pt-5 | 2xl:p-8 2xl:pt-8">
            <page-header :title="$t('global.mainNav.contacts')">
                <nio-button
                    icon="regular/download-bottom"
                    tag="router-link"
                    :to="{ name: 'contacts.import.upload'}"
                >
                    {{ $t('global.general.import') }}
                </nio-button>

                <nio-button color="blue" icon="regular/add" @click="triggerCreate">
                    {{ $t('global.general.add') }}
                </nio-button>
            </page-header>

            <content-box :loading="$wait.is('fetching.contacts')">
                <template #header>
                    <content-box-tabs :active-tab.sync="filters.tab" :tabs="tabs" />
                    <content-box-search :placeholder="$t('contacts.contactsList.search')" v-model="filters.search" />
                </template>
                <template #body>
                    <nio-table
                        :columns="columns"
                        :rows="contacts"
                        :selections.sync="selections"
                        ref="body"
                    >
                        <template #no-results>
                            <figure class="mb-6 text-3xl select-none">
                                ¯\_(ツ)_/¯
                            </figure>
                            <p class="mb-2 text-xl">
                                {{ $t('contacts.contactsList.noContacts') }}
                            </p>
                            <p class="mb-4">
                                {{ $t('contacts.contactsList.wouldYouLikeToAddOne') }}
                            </p>
                            <nio-button color="blue" icon="regular/add" @click="triggerCreate">
                                {{ $t('global.general.add') }}
                            </nio-button>
                        </template>

                        <template v-slot:[`column-${column.name}`]="{ row: contact }" v-for="column in columns">
                            <contact-list-item
                                :column="column.name"
                                :contact="contact"
                                :truncate="column.truncate"
                                :key="column.name"
                                @create-appointment="triggerCreateAppointment(contact)"
                                @create-payment="triggerCreatePayment(contact)"
                                @delete-contact="triggerDelete(contact)"
                                @update-contact="triggerUpdate(contact)"
                            />
                        </template>
                    </nio-table>
                </template>
                <template #footer>
                    <div class="flex-1 flex items-center justify-start p-4">
                        <nio-dropdown :disabled="!selectedContacts.length" placement="top-start">
                            <template #trigger>
                                <nio-button color="blue">
                                    {{ $t('contacts.contactsList.bulkAction') }}
                                    <nio-icon class="text-xs ml-2" name="regular/arrow-up-1" />
                                </nio-button>
                            </template>

                            <nio-dropdown-item
                                class="hover:text-red-500"
                                :disabled="selectedContactHasRelatedId"
                                icon="regular/bin-2-alternate"
                                @click="openDelete = true"
                            >
                                {{ $t('contacts.contactsList.delete') }}
                            </nio-dropdown-item>
                            <div v-tooltip="missingValidProviderPhoneTooltip()">
                                <nio-dropdown-item
                                    :disabled="!userValidProviderPhones"
                                    divided-top
                                    icon="regular/conversation-text"
                                    @click="$modal.open('sendMassSms')"
                                >
                                    {{ $t('contacts.contactsList.sendMassSms') }}
                                </nio-dropdown-item>
                            </div>
                        </nio-dropdown>
                    </div>

                    <div class="flex-1 flex items-center justify-center p-4">
                        <pagination
                            :current-page="pagination.currentPage"
                            :last-page="pagination.lastPage"
                            @update="setPaginationPage"
                            v-if="pagination.total"
                        />
                    </div>

                    <div class="flex-1 flex items-center justify-end p-4">
                        <pagination-info
                            :current="pagination.currentPage"
                            :per-page="perPage"
                            :total="pagination.total"
                            v-if="pagination.total"
                        />
                    </div>
                </template>
            </content-box>
        </div>

        <contact-modal />
        <appointment-modal />
        <payment-modal />
        <send-mass-sms-contacts-modal @confirm="reset" />
        <confirm-modal
            :opened.sync="openDelete"
            :title="$t('contacts.contactsList.deleteModal.header')"
            @confirm="deleteSelected"
            @deny="toDelete = null"
        >
            <p slot="message">
                {{ $t('contacts.contactsList.deleteModal.message') }}
            </p>
        </confirm-modal>

        <filters-in-url :filters="filters" />
    </div>
</template>

<script>
    import debounce from 'lodash/debounce.js';
    import { mapState, mapActions, mapMutations } from 'vuex';
    import AppointmentModal from '@/components/Modals/AppointmentModal.vue';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import ContactListItem from '@/views/Contacts/ContactListItem.vue';
    import ContactModal from '@/components/Modals/ContactModal.vue';
    import ContentBox from '@/components/ContentBox.vue';
    import ContentBoxSearch from '@/components/ContentBoxSearch.vue';
    import ContentBoxTabs from '@/components/ContentBoxTabs.vue';
    import FiltersInUrl from '@/components/Utils/FiltersInUrl.js';
    import PaymentModal from '@/components/Modals/PaymentModal.vue';
    import PageHeader from '@/components/PageHeader.vue';
    import Pagination from '@/components/Pagination.vue';
    import PaginationInfo from '@/components/PaginationInfo.vue';
    import SendMassSmsContactsModal from '@/components/Modals/SendMassSmsContactsModal.vue';

    export default {
        components: {
            AppointmentModal,
            ConfirmModal,
            ContactListItem,
            ContactModal,
            ContentBox,
            ContentBoxSearch,
            ContentBoxTabs,
            FiltersInUrl,
            PaymentModal,
            PageHeader,
            Pagination,
            PaginationInfo,
            SendMassSmsContactsModal,
        },

        data() {
            return {
                loaded: false,
                openDelete: false,
                selections: [],
                toDelete: null,
                perPage: 25,
                filters: {
                    search: '',
                    tab: 'all',
                },
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('contact', [
                'selectedContacts',
                'contacts',
                'pagination',
            ]),

            subscribed() {
                switch (this.filters.tab) {
                    case 'subscribed':
                        return 1;
                    case 'unsubscribed':
                        return 0;
                    default:
                        return null;
                }
            },

            selectedContactHasRelatedId() {
                return this.selectedContacts.some(c => !!c.related_id);
            },

            tabs() {
                return [
                    {
                        value: 'all',
                        text: this.$t('contacts.contactsList.allTab'),
                        icon: 'regular/navigation-menu',
                    },
                    {
                        value: 'subscribed',
                        text: this.$t('contacts.contactsList.subscribedTab'),
                        icon: 'regular/check-circle-1',
                    },
                    {
                        value: 'unsubscribed',
                        text: this.$t('contacts.contactsList.unsubscribedTab'),
                        icon: 'regular/remove-circle',
                    },
                ];
            },

            columns() {
                const columns = [
                    {
                        name: 'name',
                        label: this.$t('contacts.contactsList.fullName'),
                        icon: 'regular/single-neutral',
                        className: 'flex-3',
                    },
                    {
                        name: 'phone',
                        label: this.$t('contacts.contactsList.phone'),
                        icon: 'regular/phone',
                        className: 'flex-2',
                    },
                    {
                        name: 'email',
                        label: this.$t('contacts.contactsList.email'),
                        icon: 'regular/envelope',
                        className: 'flex-2',
                    },
                    {
                        name: 'locale',
                        label: this.$t('contacts.contactsList.locale'),
                        icon: 'regular/earth-1',
                        className: 'flex-2',
                    },
                    {
                        name: 'subscribed',
                        label: this.$t('contacts.contactsList.subscribed'),
                        icon: 'regular/check-circle-1',
                        className: 'flex-1',
                        truncate: false,
                    },
                    {
                        name: 'actions',
                        label: this.$t('contacts.contactsList.actions'),
                        icon: 'regular/flash-1',
                        className: 'flex-1',
                        align: 'center',
                        truncate: false,
                    },
                ];

                if (this.hasCaslFeature) {
                    columns.splice(5, 0, {
                        name: 'consent',
                        label: this.$t('contacts.contactsList.consent'),
                        icon: 'regular/check-circle-1',
                        className: 'flex-1',
                        truncate: false,
                    });
                }

                return columns;
            },

            hasCaslFeature() {
                return this.$feature.isEnabled('casl_compliance', this.authUser);
            },

            userValidProviderPhones() {
                return this.authUser.account.userAvailableProviderPhones(this.authUser.id).length > 0;
            },
        },

        watch: {
            'pagination.currentPage'() {
                this.scrollTop();
                this.fetch();
            },

            'filters.tab'() {
                this.fetch();
            },

            'filters.search'() {
                this.$wait.start('fetching.contacts');

                if (this.loaded) {
                    this.debounceFetch();
                } else {
                    this.fetch();
                }
            },

            selections(newValue) {
                const contacts = this.contacts.filter(a => newValue.includes(a.id));

                this.setSelectedContacts(contacts);
            },

            'authUser.account.id'(newValue) {
                if (!newValue) {
                    return;
                }

                this.fetch();
            },
        },

        methods: {
            ...mapActions('contact', [
                'loadContacts',
                'deleteContact',
            ]),

            ...mapMutations('contact', [
                'setEditingContact',
                'setSelectedContacts',
                'setPaginationPage',
            ]),

            getPhone(contact) {
                const validPhone = contact.phones.find(p => p.valid && p.type == 'mobile');
                const unvalidatedPhone = contact.phones.find(p => !p.validated);

                if (validPhone) {
                    return validPhone;
                } if (unvalidatedPhone) {
                    return unvalidatedPhone;
                }

                return contact.phones[0];
            },

            reset() {
                this.selections = [];
                this.toDelete = null;
            },

            async fetch() {
                this.$wait.start('fetching.contacts');

                const payload = {
                    search: this.filters.search,
                    subscribed: this.subscribed,
                    page: this.pagination.currentPage,
                    perPage: this.perPage,
                };

                this.reset();

                try {
                    await this.loadContacts(payload);
                } finally {
                    this.$wait.end('fetching.contacts');
                }
            },

            debounceFetch() {
                this.setPaginationPage(1);
                this.fetch();
            },

            triggerCreate() {
                this.$modal.open('contact');
            },

            triggerUpdate(contact) {
                this.setEditingContact(contact);
                this.$modal.open('contact');
            },

            triggerDelete(contact) {
                this.toDelete = contact;
                this.openDelete = true;
            },

            triggerCreateAppointment(contact) {
                this.$modal.open('appointment', { contact });
            },

            triggerCreatePayment(contact) {
                this.$modal.open('payment', { contact });
            },

            async deleteSelected() {
                const deletePromises = [];
                let contacts = this.selectedContacts;

                if (this.toDelete) {
                    contacts = [this.toDelete];
                }

                contacts.forEach(contact => {
                    deletePromises.push(this.deleteContact(contact.id));
                });

                try {
                    await Promise.all(deletePromises);

                    this.$notify.show({
                        type: 'success',
                        message: this.$tc('contacts.contactsList.successes.deleted', deletePromises.length),
                    });

                    this.reset();
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$tc('contacts.contactsList.errors.deleting', deletePromises.length),
                    });

                    throw error;
                }
            },

            scrollTop() {
                if (this.$refs.body) {
                    this.$refs.body.$el.scrollTop = 0;
                }
            },

            missingValidProviderPhoneTooltip() {
                return this.userValidProviderPhones ? '' : this.$t('global.disabled.missingValidProviderPhoneTooltip');
            },
        },

        created() {
            this.debounceFetch = debounce(this.debounceFetch, 300);
        },

        mounted() {
            this.fetch();

            this.$nextTick(() => {
                this.loaded = true;
            });
        },
    };
</script>
